import { create } from "zustand";
import { MapMarker } from "../components/map/map.types";

/**
 * Dedicated, lightweight store for the "Create Temporary Route from Skipped
 * Orders" flow. Deliberately NOT reusing region-management-store: that store
 * carries a large amount of region-management-specific state (filters, table
 * row focus, unassigned-locations sequencing, move dialogs, etc.) this flow
 * has no business depending on.
 */
export interface CreateTempRouteStore {
  date: string;
  candidates: MapMarker[];
  lastSequence: number;
  mapZoom: number;
  mapCentering: MapMarker[];
  driverId: string;
  routeName: string;
  color: string;
  /** Search Address box on the sequence page. */
  query: string;
  init: (date: string, locations: MapMarker[]) => void;
  setAddressSequence: (id: string, sequence?: number) => void;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  setMapZoom: (zoom: number) => void;
  setMapCentering: (addresses?: MapMarker[]) => void;
  setDriverId: (driverId: string) => void;
  setRouteName: (routeName: string) => void;
  setColor: (color: string) => void;
  setQuery: (query: string) => void;
  reset: () => void;
}

const initialState = {
  date: "",
  candidates: [] as MapMarker[],
  lastSequence: 1,
  mapZoom: 13,
  mapCentering: [] as MapMarker[],
  driverId: "",
  routeName: "",
  color: "",
  query: "",
};

const useCreateTempRouteStore = create<CreateTempRouteStore>((setState) => ({
  ...initialState,
  init: (date, locations) =>
    setState({
      ...initialState,
      date,
      candidates: locations,
    }),
  setAddressSequence: (id, sequence) =>
    setState((state) => {
      const target = state.candidates.find((c) => c.id === id);
      if (!target) return state;

      const isCurrentlySequenced =
        target.locationSequence !== null &&
        target.locationSequence !== undefined;

      if (!isCurrentlySequenced) {
        const assignedSequence = sequence && sequence > 0 ? sequence : 1;
        const updated = state.candidates.map((c) =>
          c.id === id ? { ...c, locationSequence: assignedSequence } : c,
        );
        const highestSequence = Math.max(
          0,
          ...updated
            .map((c) => c.locationSequence)
            .filter((seq): seq is number => seq !== null && seq !== undefined),
        );
        return {
          candidates: updated,
          lastSequence: highestSequence + 1,
        };
      }

      // Removing a sequence: clear it and shift down everything sequenced
      // after it so the remaining sequence stays contiguous from 1.
      const sequenceToRemove = target.locationSequence as number;
      const updated = state.candidates.map((c) => {
        if (c.id === id) {
          return { ...c, locationSequence: null };
        }
        if (
          c.locationSequence !== null &&
          c.locationSequence !== undefined &&
          c.locationSequence > sequenceToRemove
        ) {
          return { ...c, locationSequence: c.locationSequence - 1 };
        }
        return c;
      });
      const highestSequence = Math.max(
        0,
        ...updated
          .map((c) => c.locationSequence)
          .filter((seq): seq is number => seq !== null && seq !== undefined),
      );
      return {
        candidates: updated,
        lastSequence: highestSequence + 1,
      };
    }),
  moveItem: (dragIndex, hoverIndex) =>
    setState((state) => {
      const sequenced = state.candidates
        .filter(
          (c) =>
            c.locationSequence !== null && c.locationSequence !== undefined,
        )
        .sort(
          (a, b) =>
            (a.locationSequence as number) - (b.locationSequence as number),
        );

      const items = [...sequenced];
      const [dragged] = items.splice(dragIndex, 1);
      if (!dragged) return state;
      items.splice(hoverIndex, 0, dragged);

      const seqById = new Map(items.map((item, index) => [item.id, index + 1]));

      const updated = state.candidates.map((c) =>
        seqById.has(c.id)
          ? { ...c, locationSequence: seqById.get(c.id) as number }
          : c,
      );

      return { candidates: updated };
    }),
  setMapZoom: (zoom) => setState({ mapZoom: zoom }),
  setMapCentering: (addresses) => setState({ mapCentering: addresses ?? [] }),
  setDriverId: (driverId) => setState({ driverId }),
  setRouteName: (routeName) => setState({ routeName }),
  setColor: (color) => setState({ color }),
  setQuery: (query) => setState({ query }),
  reset: () => setState(initialState),
}));

export default useCreateTempRouteStore;
